
import { defineComponent, PropType } from 'vue';
import {
    Dialog,
    DialogDescription,
    DialogOverlay,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
} from '@headlessui/vue';
import navigationStore from '@/shared/navigation/navigation.store';

export default defineComponent({
    name: 'DrawerDialog',
    components: { Dialog, DialogOverlay, DialogTitle, DialogDescription, TransitionRoot, TransitionChild },
    props: {
        title: {
            type: String,
            required: false,
            default: '',
        },
        position: {
            type: String,
            required: true,
            validator: (value: string) => ['top', 'right', 'bottom', 'left', 'navigation'].includes(value),
        },
        showBackdrop: {
            type: Boolean,
            required: false,
            default: true,
        },
        description: {
            type: String,
            required: false,
            default: '',
        },
        initialFocusElement: {
            type: Object as PropType<HTMLElement | undefined>,
            required: false,
            default: undefined,
        },
        titleClasses: {
            type: String,
            required: false,
            default: '',
        },
        showCloseButton: {
            type: Boolean,
            required: false,
            default: true,
        },
        centerContent: {
            type: Boolean,
            required: false,
            default: false,
        },
        positionClass: {
            type: String,
            required: false,
            default: '',
        },
        isOpen: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['update:isOpen', 'close', 'visible'],
    setup(props, { emit }) {
        const drawerPositionStyling: string = getDrawerStyling();
        const isTopOrBottomPosition: boolean = props.position === 'top' || props.position === 'bottom' || props.position === 'navigation';
        const drawerAnimation = getDrawerAnimation();

        function getDrawerStyling(): string {
            switch (props.position) {
            case 'top':
                return 'w-full bg-white z-1';
            case 'right':
                return 'fixed inset-0 left-auto bg-white z-1 md:w-380';
            case 'bottom':
                return 'fixed inset-0 top-auto bg-white z-1';
            case 'left':
                return 'mr-auto ml-0 horizontal-drawer-width bg-white z-1 md:w-380';
            case 'navigation':
                return 'w-full bg-white z-1';
            default:
                return '';
            }
        }

        function getDrawerAnimation(): Record<string, string> {
            switch (props.position) {
            case 'top':
                return {
                    enterFrom: 'transform -translate-y-full',
                    enterTo: 'transform translate-y-0',
                    leaveFrom: 'transform translate-y-0',
                    leaveTo: 'transform -translate-y-full',
                };
            case 'right':
                return {
                    enterFrom: 'transform translate-x-full',
                    enterTo: 'transform translate-x-0',
                    leaveFrom: 'transform translate-x-0',
                    leaveTo: 'transform translate-x-full',
                };
            case 'bottom':
                return {
                    enterFrom: 'transform translate-y-full',
                    enterTo: 'transform translate-y-0',
                    leaveFrom: 'transform translate-y-0',
                    leaveTo: 'transform translate-y-full',
                };
            case 'left':
                return {
                    enterFrom: 'transform -translate-x-full',
                    enterTo: 'transform translate-x-0',
                    leaveFrom: 'transform translate-x-0',
                    leaveTo: 'transform -translate-x-full',
                };
            case 'navigation':
                return {
                    enterFrom: 'transform -translate-y-full',
                    enterTo: 'transform translate-y-0',
                    leave: 'duration-200 ease-in delay-200',
                    leaveFrom: 'transform translate-y-0',
                    leaveTo: 'transform -translate-y-full',
                };
            default:
                return {
                    enterFrom: 'opacity-0',
                    enterTo: 'opacity-100',
                    leaveFrom: 'opacity-100',
                    leaveTo: 'opacity-0',
                };
            }
        }

        return {
            closeModal() {
                emit('update:isOpen', false);
                emit('close');
                navigationStore.clearActiveMainNavigation();
            },
            onDialogOpen(): void {
                emit('visible', true);
            },
            onDialogClosed(): void {
                emit('visible', false);
            },
            drawerPositionStyling,
            isTopOrBottomPosition,
            drawerAnimation,
        };
    },
});
